import type { Anchor, EventTimes, ResolvedWindow, WindowInput } from './types';

const resolveEdge = (
  timestamp: string | null,
  anchor: Anchor,
  event: EventTimes
): Date | null => {
  if (anchor === 'event_start') {
    return event.start_date ? new Date(event.start_date) : null;
  }
  if (anchor === 'event_end') {
    return event.end_date ? new Date(event.end_date) : null;
  }
  return timestamp ? new Date(timestamp) : null;
};

export function resolveWindow(input: WindowInput, event: EventTimes): ResolvedWindow {
  return {
    from: resolveEdge(input.from, input.from_anchor, event),
    until: resolveEdge(input.until, input.until_anchor, event),
  };
}

export function isWithinWindow(
  input: WindowInput,
  event: EventTimes,
  now: Date = new Date()
): boolean {
  const { from, until } = resolveWindow(input, event);
  if (from && until && from.getTime() > until.getTime()) return false;
  if (from && now.getTime() < from.getTime()) return false;
  if (until && now.getTime() > until.getTime()) return false;
  return true;
}

export type WindowStatus = 'upcoming' | 'open' | 'closed';

/**
 * Three-state availability status for a window, resolving anchored edges
 * against the parent event. Use this for participant-facing test/survey cards
 * instead of reading the raw `available_from`/`available_until` columns – those
 * are stale snapshots for anchored edges (e.g. after an event is duplicated).
 */
export function windowStatus(
  input: WindowInput,
  event: EventTimes,
  now: Date = new Date()
): WindowStatus {
  const { from, until } = resolveWindow(input, event);
  if (from && now.getTime() < from.getTime()) return 'upcoming';
  if (until && now.getTime() > until.getTime()) return 'closed';
  return 'open';
}
